<script lang="ts" setup>
	import useProductActivationApi from '~/services/apis/product-activation'
	import { useResourcesApi } from '~/services/apis/resources'
	import type { NeedToKnowProps } from '~/components/Shared/need-to-know.vue'
	import type { BatchUploadSteps } from '~/components/Uploads/batch.vue'
	import StringMethods from '~/utils/character'
	import { filterObject } from '~/utils/functions'
	import type { AddToPlanProps } from '~/pages/manage-custodian/[purchase_id]/add/new.vue'

	const props = defineProps<AddToPlanProps>()

	const { convertNumberToText } = StringMethods()
	const { internationalNoFormat } = useCurrency()
	const { addGadgetCustodian } = useProductActivationApi()
	const { urlQuery } = useUtils()
	const { validateSerialNumber } = useResourcesApi()
	const { User } = reactor(useUser())
	// const MINIMUM_GADGET_VALUE = 50000

	const emits = defineEmits<{
		(event: 'update:form', payload: unknown[]): void
		(event: 'update:viewStaff', payload: boolean): void
		(event: 'done', response: NeedToKnowProps): void
	}>()

	const batchUploadGuide: BatchUploadSteps = {
		'Start here': {
			desc: 'Download the CSV/ XLS file template below',
			key: 'start'
		},
		'Fill Details': {
			desc: 'Fill in the CSV/ XLS file you just downloaded with your staff information which should include <span class="text-gray-600 font-medium">First name,Last name,Email address, Phone number, and the Number of Staff Dependants</span>.  You can use <a href="https://docs.google.com/spreadsheets" target="_blank" class="text-blue-500 font-medium">Google sheet</a> to edit the template or save it to your desktop or any folder on your PC and edit with excel software. See sample below:',
			key: 'details'
		},
		'Upload saved file ': {
			desc: 'Upload or drag & drop the saved CSV/ XLS file you just filled on your PC.',
			key: 'upload'
		}
	}
	const fetchingSlots = shallowRef(false)
	const isSubmitting = shallowRef(false)
	const formToSubmit = computed(() => props.form)
	const planInformation = computed(() => {
		return props.purchaseInfo ?? null
	})
	const { numberOfStaffAdded, totalStaffPendingSlot } = usePurchaseInfo({ planInformation })
	const totalStaffAdded = computed(() => numberOfStaffAdded.value + Number(props.form?.length))
	const totalStaffPending = computed(() =>
		totalStaffPendingSlot.value > 0 ? totalStaffPendingSlot.value - Number(props.form?.length) : 0
	)
	const needToKnow = computed<NeedToKnowProps>(() => [
		{
			description: `Congratulations! You have now added  ${numToTxt(totalStaffAdded.value ?? 0)} (${
				totalStaffAdded.value ?? 0
			}) policy holder(s) to your gadget plan.`,
			icon: {
				dir: 'communication',
				name: 'monitor-mobile',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600'
		},
		{
			description:
				'Policy holders that have been added to your gadgets will receive an onboarding email to activate their gadget and inspect them.',
			icon: {
				dir: 'fill',
				name: 'people',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600'
		},
		{
			description: ` ${numToTxt(totalStaffPending.value)} (${totalStaffPending.value}) of your Gadgets do not have policy holders, you will need to activate and inspect them.`,
			icon: {
				dir: 'other',
				name: 'search',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600',
			wrapperClass: {
				hidden: !totalStaffPending.value
			}
		},
		{
			description: 'Gadgets will not be covered until they are activated and inspected',
			icon: {
				dir: 'fill',
				name: 'question-2',
				pathClass: 'fill-warning-500',
				class: 'bg-warning-50'
			},
			descClass: 'text-warning-500 italic'
		}
	])
	const tableColumns = computed<TableColumn[]>(() => {
		const staffColumn: TableColumn[] = !props.uploadFromEmployees
			? [
					{
						key: 'first_name',
						dataIndex: 'first_name',
						title: 'First Name',
						class: 'min-w-[20%]'
					},
					{
						key: 'last_name',
						dataIndex: 'last_name',
						title: 'Last Name',
						class: 'min-w-[20%]'
					},
					{
						key: 'email',
						dataIndex: 'email',
						title: 'Email Address',
						class: 'min-w-[20%]'
					},
					{
						key: 'phone_number',
						dataIndex: 'phone',
						title: 'Phone Number',
						class: 'min-w-[20%]'
					}
				]
			: [
					{
						key: 'employee_id',
						dataIndex: 'employee_id',
						title: props.viewStaff ? 'Staff' : 'Staff List',
						className: 'min-w-[200px]'
						// customHeaderCell: () => ({className: 'min-w-[200px]'})
					}
				]
		return [
			{ key: 'sn', title: 'S/N', class: 'text-center' },
			{ key: 'gadget_type', title: 'Type', class: 'min-w-[20%]' },
			{ key: 'gadget_brand', title: 'Brand', class: 'min-w-[20%]' },
			{ key: 'gadget_model', title: 'Model', class: 'min-w-[20%]' },
			{ key: 'gadget_value', title: 'Value', class: 'min-w-[20%]' },
			{
				key: 'gadget_owner',
				title: 'Gadget Owner',
				class: 'min-w-[20%]'
			},
			{
				key: 'serial_number',
				title: 'Serial Number',
				class: 'min-w-[20%]'
			},
			...staffColumn,
			{ key: 'action', title: 'Action' }
		]
	})
	const { attachStaffInfo } = useStaffList()
	const fieldValidator = async (payload: Record<string, DynamicTableRecord>, fn: Function) => {
		let errMsg = ''
		const response = await validateSerialNumber({ serial_number: String(payload?.serial_number?.value) })
		if (response?.data.exists) {
			errMsg = 'The provided mobile serial number is already registered.'
		}

		if (errMsg) {
			fn(Promise.reject(errMsg))
			return useNotification().alert({
				message: errMsg,
				status: 400
			})
		}
		fn(Promise.resolve(true))
	}
	function numToTxt(payload: number) {
		return convertNumberToText(payload)
	}
	const setFields = async () => {
		return resolve(async () => {
			const result = await props.fetchEmptySlot()
			const validation = {
				required: true
			}
			if (result?.items.length) {
				result.items = result.items.map((item: unknown) => {
					const staffDetails = props.uploadFromEmployees
						? {
								employee_id: {
									value: null,
									type: 'select',
									class: 'min-w-[150px]',
									searchable: true,
									options: [],
									validations: { required: true, message: 'Select a staff from the list.' }
								}
							}
						: {
								first_name: {
									value: '',
									validations: validation
								},
								last_name: {
									value: '',
									validations: validation
								},
								phone_number: {
									value: '',
									type: 'tel',
									validations: {
										...validation,
										min: 11
									}
								},
								email: {
									value: '',
									validations: {
										type: 'email',
										...validation
									}
								}
							}
					return {
						sn: true,
						temp_id: item.temp_id as string,
						isUsed: item?.is_used,
						gadget_type: {
							value: item?.meta?.gadget_type?.name ?? '---',
							disabled: true,
							class: '!min-w-[70px]',
							type: 'select'
						},
						gadget_brand: {
							value: item?.meta?.gadget_brand?.name,
							disabled: true,
							type: 'select',
							class: 'min-w-[20%]',
							searchable: true
						},
						gadget_model: {
							value: item?.meta?.gadget_model ?? '',
							required: true,
							disabled: true,
							class: '!min-w-[80px]'
						},
						gadget_value: {
							value: internationalNoFormat(item?.meta?.gadget_value ?? 0, '₦'),
							type: 'value',
							disabled: true,
							class: '!min-w-[80px]'
						},
						gadget_owner: {
							value: User.value?.customer?.name ?? '',
							validations: validation
						},
						serial_number: {
							value: '',
							type: 'text',
							validations: {
								...validation,
								min: 5
							}
						},
						...staffDetails,
						isActive: true,
						editable: true,
						done: false
					}
				})
			}
			return result
		}, fetchingSlots)
	}
	const proceedAfterSubmission = () => {
		// @ts-ignore
		emits('done', needToKnow.value)
	}
	const submitForm = async () => {
		const payload = {
			purchase_id: String(props.purchaseId),
			details: formToSubmit.value.map((item, i) => {
				const cleaned = filterObject<{
					phone_number: string
					first_name: string
					last_name: string
					temp_id: string
					email: string
				}>({ ...item, row_number: i + 1 }, ['done', 'editable', 'isActive', 'isUsed', 'sn', 'loading', 'key'])
				cleaned.phone_number = replacePhoneNumberToNaija(cleaned.phone_number)
				return cleaned
			})
		} as AddGadgetCustodianPayload
		resolve(async () => {
			const res = await addGadgetCustodian(payload)
			if (res?.data) {
				useNotification().alert({
					status: 200,
					message: res?.responseText || ''
				})
				proceedAfterSubmission()
			}
		}, isSubmitting)
	}
	const { screenSizesBreak } = useMobile()
</script>

<template>
	<section>
		<forms-policy-activation-batch-upload
			v-if="urlQuery?.method === 'batch'"
			preview-img="/images/png/gadget-custodian-upload.png"
			file-name="gadget-custodian-upload-template"
			:plan-id="String(props.purchaseId)"
			:upload-steps="batchUploadGuide"
			@done="proceedAfterSubmission"
		/>
		<template v-else-if="!viewStaff">
			<uploads-add-staff-block
				v-if="!screenSizesBreak.biggerThanMd"
				:form="form"
				:data="setFields"
				:skip-user-verification="Boolean(uploadFromEmployees)"
				:submitting="isSubmitting"
				@update:value="$emit('update:form', $event)"
				@validator="fieldValidator"
				@done="submitForm"
			/>

			<uploads-add-staff-table
				v-else
				:records="setFields"
				:table-columns="tableColumns"
				:loading="loading || fetchingSlots"
				:submitting="isSubmitting"
				:skip-user-verification="Boolean(uploadFromEmployees)"
				:options="{ pagination: true, filter: { page: 1 } }"
				:deletable="false"
				file-name="add-staff-template"
				class="mt-10 w-full"
				@update:value="e => $emit('update:form', e)"
				@validator="fieldValidator"
				@done="submitForm"
			>
				<template #employee_id="{ column, record }">
					<forms-staff-list
						v-if="record?.['employee_id'] && uploadFromEmployees"
						v-model:value="(record.employee_id as DynamicTableRecord).value"
						placeholder="Pick from employee list"
						:purchase-id="purchaseId"
						:name="String(column ?? 'staff-list')"
						@handle-change="e => attachStaffInfo(e, record as Record<string, DynamicTableRecord>)"
					/>
				</template>
				<template #filled-employee_id="{ record }">
					{{ (record?.first_name as DynamicTableRecord)?.value ?? '' }}
					{{ (record?.last_name as DynamicTableRecord)?.value ?? '+1' }}
				</template>
			</uploads-add-staff-table>
		</template>

		<ui-data-table v-else :columns="tableColumns" :records="fetchEmptySlot" :loading="fetchingSlots" class="mt-16">
			<template #bodyCell="{ column: { key }, record }">
				<span v-if="key === 'employee_id'">
					{{ record?.meta?.first_name ?? record?.first_name ?? 'N/A' }}
					{{ record?.meta?.last_name ?? record?.last_name ?? '' }}
				</span>
				<span v-if="key === 'first_name'">{{ record?.meta?.first_name ?? record?.first_name ?? 'N/A' }}</span>
				<span v-if="key === 'last_name'">{{ record?.meta?.last_name ?? record?.last_name ?? 'N/A' }}</span>
				<span v-if="key === 'email'">{{ record?.meta?.email ?? record?.email ?? 'N/A' }}</span>
				<span v-if="key === 'phone_number'">{{
					record?.meta?.phone_number ?? record?.phone_number ?? 'N/A'
				}}</span>
				<span v-if="key === 'gadget_brand'">{{ record?.meta?.gadget_brand?.name ?? 'N/A' }}</span>
				<span v-if="key === 'gadget_model'">{{ record?.meta?.gadget_model ?? 'N/A' }}</span>
				<span v-if="key === 'gadget_owner'">{{ record?.meta?.purchase_by ?? 'N/A' }}</span>
				<span v-if="key === 'serial_number'">{{ record?.meta?.serial_number ?? 'N/A' }}</span>
				<span v-if="key === 'gadget_type'">{{ record?.meta?.gadget_type?.name ?? 'N/A' }}</span>
				<span v-if="key === 'gadget_value'">
					{{ internationalNoFormat(record?.meta?.gadget_value ?? 0, '₦') }}
				</span>
				<span v-if="key === 'action'">
					<button type="button" class="0 default-btn-sm mx-auto bg-primary-100 text-white" disabled>
						<lazy-tools-icon
							dir="fill"
							icon="check-2"
							:height="18"
							:width="18"
							path-class="stroke-white fill-transparent"
							class="mr-2"
						/>
						<span>Added</span>
					</button>
				</span>
			</template>
		</ui-data-table>
	</section>
</template>
